import {
  Box,
  CssBaseline,
  createTheme,
  ThemeProvider as MuiThemeProvider,
  useTheme,
} from "@mui/material";
import { enUS as coreEnUs, ptBR as corePtBr } from "@mui/material/locale";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  enUS as datePickerEnUs,
  ptBR as datePickerPtBr,
} from "@mui/x-date-pickers/locales";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import "dayjs/locale/en";
import "dayjs/locale/pt-br";
import {
  enUS as frontComponentsEnUs,
  ptBR as frontComponentsPtBr,
  ThemeProvider,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { SnackbarProvider } from "notistack";
import { Suspense, useEffect, useMemo, useState } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import { CookieConsent } from "./components/molecules/CookieConsent";
import { FullScreenLoading } from "./components/molecules/FullScreenLoading";
import { AnnouncementBanner } from "./components/organisms/AnnouncementBanner";
import { AnnouncementModal } from "./components/organisms/AnnouncementModal";
import { LeftMenu } from "./components/organisms/LeftMenu";
import { TopMenu } from "./components/organisms/TopMenu";
import { Page404 } from "./components/templates/Page404";
import { initializeZodLocale } from "./config/zodLocale";
import { useAuth } from "./hooks/useAuth";
// biome-ignore lint/style/noRestrictedImports: This is the initialization of i18next for the app
import i18next from "./i18n";
import { AppProvider } from "./providers/AppProvider";
import { routeTree } from "./routeTree.gen";
import { theme as appTheme } from "./theme";

const RAILS_PUBLIC_PAGES = [
  "/users/confirmation",
  "/users/confirmation/new",
  "/users/password/edit",
  "/users/password/new",
  "/users/sign_in",
  "/users/sign_up",
] as const;
const MM_REQUEST_LEGACY_INTERFACE_STORAGE_KEY = "legacy_interface";

const queryClient = new QueryClient();

const router = createRouter({
  context: {
    // biome-ignore lint/style/noNonNullAssertion: This will be set after we wrap the app in an AuthProvider
    auth: undefined!, // This will be set after we wrap the app in an AuthProvider
    queryClient,
  },
  defaultNotFoundComponent: Page404,
  defaultPreload: "intent",
  routeTree,
  scrollRestoration: true,
});

// Register things for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const MainApp: React.FC = () => {
  const auth = useAuth();
  const [leftMenuOpen, setLeftMenuOpen] = useState(true);
  const theme = useTheme();

  const [redirectToSignIn, setRedirectToSignIn] = useState(false);

  // TODO: Remove the permission check when the MM requests React screen is fully migrated.
  const canViewMmRequestsNewInterface =
    auth.user?.can_view_mm_requests_new_interface ?? false;

  useEffect(() => {
    if (
      !canViewMmRequestsNewInterface ||
      window.location.pathname !== "/vbuyer/mm_requests"
    ) {
      return;
    }

    const requestedLegacyInterface = new URLSearchParams(
      window.location.search,
    ).get("legacy_interface");

    if (
      window.reactWithRails &&
      requestedLegacyInterface !== "true" &&
      requestedLegacyInterface !== "false" &&
      localStorage.getItem(MM_REQUEST_LEGACY_INTERFACE_STORAGE_KEY) === "false"
    ) {
      window.location.replace("/vbuyer/mm_requests?legacy_interface=false");
    }
  }, [canViewMmRequestsNewInterface]);

  useEffect(() => {
    if (theme.palette.mode === "dark") {
      document.documentElement.classList.add("cc--darkmode");
    } else {
      document.documentElement.classList.remove("cc--darkmode");
    }
  }, [theme]);

  // TODO: Temporary gambit because not all routes stay in React
  useEffect(() => {
    const processSignOut = async (): Promise<void> => {
      await auth.signOut();
      window.location.pathname = "/users/sign_in";
    };

    if (
      window.reactWithRails &&
      !auth.user &&
      !RAILS_PUBLIC_PAGES.includes(
        window.location.pathname as (typeof RAILS_PUBLIC_PAGES)[number],
      )
    ) {
      setRedirectToSignIn(true);
      processSignOut();
    }
  }, [auth]);

  if (redirectToSignIn) {
    return <FullScreenLoading />;
  }

  if (window.reactWithRails) {
    return (
      <>
        {auth.user ? <TopMenu setLeftMenuOpen={setLeftMenuOpen} /> : null}
        <Box sx={{ display: "flex" }}>
          {auth.user ? (
            <>
              <LeftMenu
                leftMenuOpen={leftMenuOpen}
                setLeftMenuOpen={setLeftMenuOpen}
              />
              <Box sx={{ flexDirection: "column", flexGrow: 1 }}>
                <AnnouncementModal />
                <AnnouncementBanner />
              </Box>
            </>
          ) : null}
        </Box>
        <CookieConsent />
      </>
    );
  }

  return <RouterProvider context={{ auth }} router={router} />;
};

const AppThemeProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { i18n } = useTranslation();

  const muiThemeWithLocale = useMemo(() => {
    switch (i18n.language) {
      case "pt-BR":
        return createTheme(appTheme, corePtBr, datePickerPtBr);
      default:
        return createTheme(appTheme, coreEnUs, datePickerEnUs);
    }
  }, [i18n.language]);

  const frontComponentsTheme = useMemo(() => {
    switch (i18n.language) {
      case "pt-BR":
        return {
          components: {
            ...frontComponentsPtBr,
          },
        };
      default:
        return {
          components: {
            ...frontComponentsEnUs,
          },
        };
    }
  }, [i18n.language]);

  return (
    <MuiThemeProvider defaultMode="light" noSsr theme={muiThemeWithLocale}>
      <ThemeProvider theme={frontComponentsTheme}>
        <CssBaseline enableColorScheme />
        <LocalizationProvider
          adapterLocale={i18n.language.toLowerCase()}
          dateAdapter={AdapterDayjs}
        >
          {children}
        </LocalizationProvider>
      </ThemeProvider>
    </MuiThemeProvider>
  );
};

export const App: React.FC = () => {
  useEffect(() => {
    // Initialize Zod V4 locale configuration
    initializeZodLocale();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18next}>
        <AppThemeProvider>
          <SnackbarProvider>
            <Suspense fallback={<FullScreenLoading />}>
              <AppProvider>
                <MainApp />
              </AppProvider>
            </Suspense>
          </SnackbarProvider>
        </AppThemeProvider>
      </I18nextProvider>
    </QueryClientProvider>
  );
};
